// SVG
import { ReactComponent as LeftArrow } from "../assets/img/left-arrow.svg";
import { ReactComponent as RightArrow } from "../assets/img/right-arrow.svg";
// components
import NavMenu from "../components/NavMenu/NavMenu";
import MainNavbar from "../components/MainNavbar/MainNavbar";
import PaperKeywords from "../components/PaperKeywords/PaperKeywords";
import RepoSideBar from "../components/RepoSideBar/RepoSideBar";

import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Repository } from "../types/repository";
import { CreatePaperAndAddToRepositoryResponse, Paper } from "../types/Paper";
import {
  createKeywordAndAddToRepositoryAndPaper,
  createPaperAndAddToRepository,
  createParameterAndAddToRepositoryAndPaper,
  deleteKeywordFromPaper,
  deleteParameterFromPaper,
  getKeyWordsAndParmetersFromOpenAi,
  getKeywordsFoRepository,
  getKeywordsForPaper,
  getPapersFromArxivBySearchPhrase,
  getParametersForPaper,
  getParametersForRepository,
  listPapersInRepository,
  listRepositories,
} from "../lib/api";

import { v4 as uuidv4 } from "uuid";

import _ from "lodash";
import {
  CreateKeywordAndAddToRepositoryAndPaperResponse,
  Keyword,
} from "../types/Keyword";
import {
  CreateParameterAndAddToRepositoryAndPaperResponse,
  Parameter,
} from "../types/Parameter";
import PaperParameters from "../components/PaperParameters/PaperParameters";
import { createRepository as createRepositoryApi } from "../lib/api";
import { extractDate } from "../utils/DateTime/ExtractDate";
import {
  ArxivPaperResponse,
  constructQuery,
  parseArxivSearchResponse,
} from "../utils/arxiv";
import { parseOpenAIResponseContent } from "../utils/openAi";

type DashboardProps = {
  onNavigateToVisualization: (repository: Repository) => void;
};

const Dashboard = (props: DashboardProps) => {
  const { isLoggedIn } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();

  const { token } = useSelector((state: any) => state.auth);

  const [dropdownVisibility, setDropdownVisibility] = useState<{
    [key: string]: boolean;
  }>({});

  const [repositories, setRepositories] = useState<{
    [key: string]: Repository;
  }>({});

  const [selectedRepoId, setSelectedRepoId] = useState<string | null>(null);

  const [modalShow, setModalShow] = useState<{ [repoId: string]: boolean }>({});

  const [currentlySelectedPaper, setCurrentlySelectedPaper] =
    useState<Paper | null>(null);

  const handleVisualizationClick = (repository: Repository) => {
    props.onNavigateToVisualization(repository);
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/auth");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    // Function to fetch repositories
    const fetchRepositories = async () => {
      try {
        const repos: Repository[] = await listRepositories({ token: token }); // Replace 'API_ENDPOINT' with your actual endpoint
        const reposMap = repos.reduce<{ [key: string]: Repository }>(
          (acc, repo) => {
            acc[repo.id.toString()] = repo;
            return acc;
          },
          {}
        );
        setRepositories(reposMap); // Assuming the API returns an array of repositories
      } catch (err) {
        window.alert(`Error fetching repositories: ${err}`);
      }
    };

    fetchRepositories();
  }, [token]); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    const fetchRepositoryData = async () => {
      if (!selectedRepoId) return;

      const repo = repositories[selectedRepoId];
      if (!repo) {
        console.error(`Repository with ID ${selectedRepoId} not found.`);
        return;
      }

      if (repo.papers) return;
      let papers: Paper[] = repo.papers || [];
      if (papers.length === 0) {
        try {
          const response = await listPapersInRepository({
            repository_id: selectedRepoId,
            token: token,
          });
          papers = response as Paper[];
        } catch (err) {
          window.alert(
            `Error fetching papers for repository ${repo.name}: ${err}`
          );
        }
      }

      const updatedPapers = await Promise.all(
        papers.map(async (paper) => {
          if (!paper.keywords || paper.keywords.length === 0) {
            try {
              const keywordsResponse = await getKeywordsForPaper({
                paper_id: paper.id,
                token: token,
              });
              paper.keywords = keywordsResponse as Keyword[];
            } catch (err) {
              window.alert(
                `Error fetching keywords for paper ${paper?.name}: ${err}`
              );
            }
          }

          if (!paper.parameters || paper.parameters.length === 0) {
            try {
              const parametersResponse = await getParametersForPaper({
                repository_id: selectedRepoId,
                paper_id: paper.id,
                token: token,
              });
              paper.parameters = parametersResponse as Parameter[];
            } catch (err) {
              window.alert(
                `Error fetching parameters for paper ${paper?.name}: ${err}`
              );
            }
          }

          return paper;
        })
      );

      setRepositories((prevRepos) => ({
        ...prevRepos,
        [selectedRepoId]: {
          ...prevRepos[selectedRepoId],
          papers: updatedPapers,
        },
      }));
    };

    if (
      selectedRepoId &&
      (!repositories[selectedRepoId] || !repositories[selectedRepoId].papers)
    ) {
      fetchRepositoryData();
    }
  }, [selectedRepoId, repositories, token]);

  useEffect(() => {
    const fetchKeywordsForRepository = async () => {
      if (selectedRepoId && !repositories[selectedRepoId]?.keywords) {
        try {
          const response = await getKeywordsFoRepository({
            repository_id: selectedRepoId,
            token: token,
          });

          // Ensure response is treated as an array of Keyword objects
          const uniqueKeywords: Keyword[] = _.uniqBy(
            response as Keyword[],
            "keyword"
          );

          // Update the state with the array of unique keywords
          setRepositories((prevRepos) => ({
            ...prevRepos,
            [selectedRepoId]: {
              ...prevRepos[selectedRepoId],
              keywords: uniqueKeywords,
            },
          }));
        } catch (err) {
          const repository = repositories[selectedRepoId];
          window.alert(
            `Error fetching keywords for repository ${repository?.name}: ${err}`
          );
        }
      }
    };
    fetchKeywordsForRepository();
  }, [selectedRepoId, repositories, token]);

  useEffect(() => {
    const fetchParametersForRepository = async () => {
      if (selectedRepoId && !repositories[selectedRepoId]?.parameters) {
        try {
          const response = await getParametersForRepository({
            repository_id: selectedRepoId,
            token: token,
          });

          // Ensure response is treated as an array of Keyword objects
          const uniqueParameters: Parameter[] = _.uniqBy(
            response as Parameter[],
            "key"
          );

          // Update the state with the array of unique keywords
          setRepositories((prevRepos) => ({
            ...prevRepos,
            [selectedRepoId]: {
              ...prevRepos[selectedRepoId],
              parameters: uniqueParameters,
            },
          }));
        } catch (err) {
          const repository = repositories[selectedRepoId];
          window.alert(
            `Error fetching parameters for repository ${repository?.name}: ${err}`
          );
        }
      }
    };
    fetchParametersForRepository();
  }, [selectedRepoId, repositories, token]);

  const createRepository = async (repoName: string) => {
    try {
      // Call the API to add the paper to the repository
      // ...
      // Assuming the API returns the newly created paper object
      const newRepo: Repository = await createRepositoryApi({
        name: repoName,
        token: token,
      });
      // Update the papers state with the new paper
      setRepositories((prevRepos) => {
        const updatedRepos = { ...prevRepos };
        updatedRepos[newRepo.id.toString()] = newRepo;
        return updatedRepos;
      });
    } catch (err) {
      window.alert(`Error adding paper to repository ${repoName}: ${err}`);
    }
  };

  const searchAndCreateRepository = async (searchPhrase: string) => {
    const url = constructQuery(searchPhrase);
    try {
      const responseAsText = await getPapersFromArxivBySearchPhrase(url);
      const parsedPapers: ArxivPaperResponse[] =
        parseArxivSearchResponse(responseAsText);
      const newRepository: Repository = {
        id: "ONLY-CLIENT-STORE-REPO_" + uuidv4(),
        name: searchPhrase,
        papers: await Promise.all(
          parsedPapers.map(async (paper) => {
            const openAiResponse = await getKeyWordsAndParmetersFromOpenAi(
              paper.summary
            );
            const { extractedKeywords, extractedParameters } =
              parseOpenAIResponseContent(openAiResponse);
            return {
              id: paper.id,
              name: paper.title,
              created_at: paper.date,
              keywords: extractedKeywords,
              parameters: extractedParameters,
              link: paper.link,
            };
          })
        ),
        keywords: [],
        parameters: [],
      };

      // Update the state with the new repository
      setRepositories((prevRepos) => {
        const updatedRepos = {
          ...prevRepos,
          [newRepository.id]: newRepository,
        };
        return updatedRepos;
      });
    } catch (err) {
      window.alert(
        `Error searching and creating repository with search phrase ${searchPhrase}`
      );
      console.error(
        `Error searching and creating repository with search phrase ${searchPhrase} ${err}`
      );
    }
  };

  const addPaperToRepository = async (repoId: string, paperName: string) => {
    const repo = repositories[repoId];
    try {
      // Call the API to add the paper to the repository
      // ...
      // Assuming the API returns the newly created paper object
      const result: CreatePaperAndAddToRepositoryResponse =
        await createPaperAndAddToRepository({
          repository_id: repoId,
          name: paperName,
          token: token,
        });
      const newPaper: Paper = {
        id: result.paper_id,
        name: paperName,
        created_at: extractDate(result.created_at),
      };

      setRepositories((prevRepos) => {
        const updatedRepos = { ...prevRepos };
        if (!updatedRepos[repoId].papers) {
          updatedRepos[repoId].papers = [];
        }
        updatedRepos[repoId].papers?.push(newPaper);
        return updatedRepos;
      });
    } catch (err) {
      window.alert(`Error adding paper to repository ${repo.name}: ${err}`);
    }
  };

  const handleDropdownClick =
    (repoId: string) => async (event: React.MouseEvent) => {
      event.preventDefault();

      const isVisible = !dropdownVisibility[repoId];
      setDropdownVisibility((prevState) => ({
        ..._.mapValues(prevState, () => false), // Set all to false, then toggle the one being interacted with
        [repoId]: isVisible,
      }));

      // Update the selected repository ID if the dropdown is becoming visible
      if (isVisible) {
        setSelectedRepoId(repoId);
      } else {
        // Optionally, reset the selectedRepoId if the dropdown is being closed
        setSelectedRepoId(null);
      }
    };

  const toggleModal = (repoId: string) => {
    setModalShow((prev) => {
      const newState = { ...prev, [repoId]: !prev[repoId] };
      return newState;
    });
  };

  const hideModal = (repoId: string) => {
    setModalShow((prev) => ({ ...prev, [repoId]: false }));
  };

  const paperClickHandler =
    (repoId: string, paperId: string) => async (event: React.MouseEvent) => {
      event.preventDefault();
      const repo = repositories[repoId];
      if (!repo) {
        console.error(`Repository with ID ${repoId} not found.`);
        return;
      }
      const paper = repo.papers?.find((p) => p.id === paperId);
      if (!paper) {
        console.error(
          `Paper with ID ${paperId} not found in repository ${repoId}.`
        );
        return;
      }
      setCurrentlySelectedPaper(paper);
    };

  const addKeywordToPaper = async (
    repoId: string,
    paperId: string,
    keyword: string
  ) => {
    const repo = repositories[repoId];
    if (!repo) {
      console.error(`Repository with ID ${repoId} not found.`);
      return;
    }

    // Use optional chaining to safely access papers
    const paper = repo.papers?.find((p) => p.id === paperId);
    if (!paper) {
      console.error(
        `Paper with ID ${paperId} not found in repository ${repoId}.`
      );
      return;
    }
    try {
      // Call the API to add the paper to the repository
      // ...
      // Assuming the API returns the newly created paper object
      const response: CreateKeywordAndAddToRepositoryAndPaperResponse =
        await createKeywordAndAddToRepositoryAndPaper({
          repository_id: repoId,
          paper_id: currentlySelectedPaper?.id || "",
          keyword: keyword,
          token: token,
        });
      const newKeyword: Keyword = {
        id: response.keyword_id,
        keyword: response.keyword,
      };
      setRepositories((prevRepos) => {
        const updatedRepos = { ...prevRepos };
        const updatedPapers = updatedRepos[repoId].papers?.map((p) => {
          if (p.id === paperId) {
            return { ...p, keywords: [...(p.keywords || []), newKeyword] };
          }
          return p;
        });
        updatedRepos[repoId] = {
          ...updatedRepos[repoId],
          papers: updatedPapers,
        };
        return updatedRepos;
      });
    } catch (err) {
      window.alert(`Error adding keyword to paper ${paper?.name}: ${err}`);
    }
  };

  const removeKeywordFromPaper = async (
    repoId: string,
    paperId: string,
    keywordId: string
  ) => {
    const repo = _.get(repositories, repoId);
    if (!repo) {
      console.error(`Repository with ID ${repoId} not found.`);
      return;
    }

    const paper = _.find(repo.papers, { id: paperId });
    if (!paper) {
      console.error(
        `Paper with ID ${paperId} not found in repository ${repoId}.`
      );
      return;
    }

    try {
      // Call the API to delete the keyword from the paper
      await deleteKeywordFromPaper({
        repository_id: repoId,
        paper_id: paperId,
        keyword_id: keywordId,
        token: token,
      });

      setRepositories((prevRepos) => {
        const newRepos = _.cloneDeep(prevRepos);
        // Remove the keyword from the paper
        const keywordsPath = [
          repoId,
          "papers",
          _.findIndex(newRepos[repoId].papers, { id: paperId }),
          "keywords",
        ];
        _.remove(_.get(newRepos, keywordsPath), { id: keywordId });

        return newRepos;
      });
    } catch (err) {
      window.alert(`Error deleting keyword from paper ${paper?.name}: ${err}`);
    }
  };

  const addParameterToPaper = async (
    repoId: string,
    paperId: string,
    parameterKey: string,
    parameterValue: number,
    parameterUnit: string
  ) => {
    const repo = _.get(repositories, repoId);
    if (!repo) {
      console.error(`Repository with ID ${repoId} not found.`);
      return;
    }

    const paper = _.find(repo.papers, { id: paperId });
    if (!paper) {
      console.error(
        `Paper with ID ${paperId} not found in repository ${repoId}.`
      );
      return;
    }

    try {
      // Call the API to add the paper to the repository
      // ...
      // Assuming the API returns the newly created paper object
      const response: CreateParameterAndAddToRepositoryAndPaperResponse =
        await createParameterAndAddToRepositoryAndPaper({
          repository_id: repoId,
          paper_id: currentlySelectedPaper?.id || "",
          key: parameterKey,
          value: parameterValue,
          unit: parameterUnit,
          token: token,
        });

      if (response) {
        const newParameter: Parameter = {
          id: response.id,
          key: response.key,
          value: response.value,
          unit: response.unit,
        };

        setRepositories((prevRepos) => {
          const updatedRepos = { ...prevRepos };
          const updatedPapers = updatedRepos[repoId].papers?.map((p) => {
            if (p.id === paperId) {
              // Add new parameter to the paper
              return {
                ...p,
                parameters: [...(p.parameters || []), newParameter],
              };
            }
            return p;
          });

          updatedRepos[repoId] = {
            ...updatedRepos[repoId],
            papers: updatedPapers,
          };
          return updatedRepos;
        });
      } else {
        window.alert(`Error adding parameter to paper ${paper?.name}`);
      }
    } catch (err) {
      window.alert(`Error adding parameter to paper ${paper?.name}: ${err}`);
    }
  };

  const removeParameterFromPaper = async (
    repoId: string,
    paperId: string,
    parameterId: string
  ) => {
    const repo = _.get(repositories, repoId);
    if (!repo) {
      console.error(`Repository with ID ${repoId} not found.`);
      return;
    }

    const paper = _.find(repo.papers, { id: paperId });
    if (!paper) {
      console.error(
        `Paper with ID ${paperId} not found in repository ${repoId}.`
      );
      return;
    }

    try {
      // Call the API to delete the keyword from the paper
      await deleteParameterFromPaper({
        repository_id: repoId,
        paper_id: paperId,
        parameter_id: parameterId,
        token: token,
      });

      setRepositories((prevRepos) => {
        const updatedRepos = { ...prevRepos };
        const updatedPapers = updatedRepos[repoId].papers?.map((p) => {
          if (p.id === paperId) {
            return {
              ...p,
              parameters: p.parameters?.filter(
                (parameter) => parameter.id !== parameterId
              ),
            };
          }
          return p;
        });

        updatedRepos[repoId] = {
          ...updatedRepos[repoId],
          papers: updatedPapers,
        };
        return updatedRepos;
      });
    } catch (err) {
      window.alert(
        `Error deleting parameter from paper ${paper?.name}: ${err}`
      );
    }
  };

  return (
    <>
      <MainNavbar
        repositoryName={
          selectedRepoId ? repositories[selectedRepoId]?.name : null
        }
        paperName={currentlySelectedPaper ? currentlySelectedPaper?.name : null}
        createRepository={createRepository}
        searchAndCreateRepository={searchAndCreateRepository}
      />
      <div className="container-fluid">
        <div className="row">
          <NavMenu
            repositories={repositories}
            papers={
              selectedRepoId ? repositories[selectedRepoId]?.papers || [] : []
            }
            modalShow={modalShow}
            toggleModal={toggleModal}
            hideModal={hideModal}
            handleDropdownClick={handleDropdownClick}
            dropdownVisibility={dropdownVisibility}
            addPaperToRepository={addPaperToRepository}
            paperClickHandler={paperClickHandler}
          />

          <main
            role="main"
            className="main col-md-9 ml-sm-auto col-lg-6 pt-3 px-4"
          >
            <ul className="nav nav-tabs" id="myTab" role="tablist">
              <li className="nav-item">
                <a
                  className="nav-link active"
                  id="home-tab"
                  data-toggle="tab"
                  href="/#"
                  role="tab"
                  aria-controls="home"
                  aria-selected="true"
                >
                  {selectedRepoId
                    ? repositories[selectedRepoId]?.name
                    : "No Repository Selected"}
                </a>
              </li>
              <li className="nav-item arrow-tabs px-3">
                <button className="btn">
                  <LeftArrow />
                </button>
                <button className="btn">
                  <RightArrow />
                </button>
              </li>
              <li className="nav-item ml-auto file-links">
                <button
                  className="btn visualize"
                  onClick={() => {
                    // selectedRepoId
                    //   ? navigate("/visualization", {
                    //       state: {
                    //         repository: repositories[selectedRepoId],
                    //       },
                    //     })
                    //   : window.alert(`Please select a repository to visualize`);
                    selectedRepoId
                      ? handleVisualizationClick(repositories[selectedRepoId])
                      : window.alert(`Please select a repository to visualize`);
                  }}
                >
                  Visualize
                </button>
              </li>
            </ul>
            <div className="tab-content" id="myTabContent">
              <div
                className="tab-pane fade show active"
                id="home"
                role="tabpanel"
                aria-labelledby="home-tab"
              >
                <div className="row mb-3">
                  <div className="col-md-10">
                    <span className="title-top">Title</span>
                    <h5 className="title-main">
                      {currentlySelectedPaper
                        ? currentlySelectedPaper.name
                        : "No Paper selected"}
                    </h5>
                  </div>
                  <div className="col-md-2 text-right">
                    <span className="title-top text-right">Date</span>
                    <h5 className="title-main date text-right">
                      {currentlySelectedPaper
                        ? currentlySelectedPaper.created_at
                        : "No Paper selected"}
                    </h5>
                  </div>
                </div>
                <PaperKeywords
                  keywords={
                    selectedRepoId && currentlySelectedPaper
                      ? repositories[selectedRepoId]?.papers?.find(
                          (p) => p.id === currentlySelectedPaper.id
                        )?.keywords || []
                      : []
                  }
                  addKeywordToPaper={addKeywordToPaper}
                  removeKeywordFromPaper={removeKeywordFromPaper}
                  repoId={selectedRepoId ?? ""}
                  paperId={currentlySelectedPaper?.id ?? ""}
                />
                <PaperParameters
                  parameters={
                    selectedRepoId && currentlySelectedPaper
                      ? repositories[selectedRepoId]?.papers?.find(
                          (p) => p.id === currentlySelectedPaper.id
                        )?.parameters || []
                      : []
                  }
                  addParameterToPaper={addParameterToPaper}
                  removeParameterFromPaper={removeParameterFromPaper}
                  repoId={selectedRepoId ?? ""}
                  paperId={currentlySelectedPaper?.id ?? ""}
                />
                {/* <RepositoryMethodology /> */}
              </div>
            </div>
          </main>
          <RepoSideBar
            onSort={() => {}}
            isVisualization={false}
            repoId={selectedRepoId}
            keywords={
              selectedRepoId ? repositories[selectedRepoId]?.keywords ?? [] : []
            }
            parameters={
              selectedRepoId
                ? repositories[selectedRepoId]?.parameters ?? []
                : []
            }
          />
        </div>
      </div>
    </>
  );
};

Dashboard.propTypes = {};

Dashboard.defaultProps = {};

export default Dashboard;
